import { Link } from "react-router-dom";
import { Accessibility, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    title: "Our Commitment to Accessibility",
    body: [
      "We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.",
    ],
  },
  {
    title: "Accessibility Standards",
    body: [
      "We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.",
    ],
  },
  {
    title: "Accessibility Technology & Tools",
    body: [
      "To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:",
    ],
    list: [
      "Screen reader enhancements",
      "Keyboard navigation improvements",
      "Color contrast adjustments",
      "Text size and spacing controls",
      "Focus indicators",
      "Additional accessibility interface options",
    ],
    after:
      "This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.",
  },
  {
    title: "Ongoing Improvement",
    body: ["Accessibility is an ongoing initiative. We are:"],
    list: [
      "Reviewing site content and functionality regularly",
      "Working with developers and accessibility tools",
      "Identifying and remediating barriers",
      "Incorporating accessibility into future design and development",
    ],
    after: "We understand accessibility is a continuous journey and are committed to making improvements over time.",
  },
  {
    title: "Third-Party Content",
    body: [
      "Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.",
    ],
  },
  {
    title: "Feedback & Assistance",
    body: [
      "If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us. Please include:",
    ],
    list: ["The page URL", "Description of the issue", "Your preferred contact method"],
    after:
      "Contact: Information on our Contact Us Page. We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.",
  },
  {
    title: "Good Faith Efforts",
    body: [
      "We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.",
    ],
  },
  {
    title: "Terms & Conditions — Accessibility Clause",
    body: [
      "We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.",
    ],
  },
];

const AccessibilityStatement = () => {
  return (
    <div data-testid="accessibility-statement-page">
      <section className="bg-[#001529] blueprint-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-14">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-12 w-12 rounded-full bg-[#0284c7] flex items-center justify-center">
                <Accessibility className="h-6 w-6 text-white" aria-hidden="true" />
              </span>
              <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500">WCAG 2.1 Level AA</p>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Website Accessibility Statement
            </h1>
            <p className="mt-4 text-sm text-slate-300 font-semibold">Last Updated: February 27, 2026</p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-14 sm:pt-12 sm:pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="space-y-12">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 40, 160)}>
              <section aria-labelledby={`a11y-section-${i}`} data-testid={`a11y-statement-section-${i}`}>
                <h2
                  id={`a11y-section-${i}`}
                  className="font-heading text-xl sm:text-2xl font-bold text-slate-900 gold-underline"
                >
                  {s.title}
                </h2>
                <div className="mt-6 space-y-4 text-base text-slate-600 leading-relaxed">
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                  {s.list && (
                    <ul className="list-disc pl-6 space-y-2 marker:text-amber-600">
                      {s.list.map((li) => (
                        <li key={li}>{li}</li>
                      ))}
                    </ul>
                  )}
                  {s.after && <p>{s.after}</p>}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-14 rounded-xl bg-slate-50 border border-slate-100 p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
            <p className="text-sm text-slate-600 max-w-xl">
              Experiencing an accessibility barrier? We welcome your feedback and will provide assistance or an
              accessible alternative.
            </p>
            <Link
              to="/contact"
              data-testid="a11y-statement-contact-button"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-3 rounded-md transition-colors duration-200 shrink-0"
            >
              Contact us <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default AccessibilityStatement;
